<script lang="ts">
export default {
  name: 'PromptMappingPage',
};
</script>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import Card from '@/components/ui/card/Card.vue';
import Badge from '@/components/ui/Badge.vue';
import Separator from '@/components/ui/Separator.vue';
import { aiService } from '@/services/AIService';
import { referenceService } from '@/services/api';
import { documentService } from '@/services/v2/DocumentService';
import type { AIPromptMapping, AIPrompt } from '@/types/ai';
import type { LabelValue } from '@/types/label-value';
import type { DocumentType } from '@/types/v2/Document';
import { Pencil, Plus, X } from 'lucide-vue-next';
import AddEditPromptMappingModal from '@/components/ui/AddEditPromptMappingModal.vue';
import UIButton from '@/components/ui/UIButton.vue';

interface PromptMappingDisplay extends AIPromptMapping {
  promptName?: string;
  coverageTypeName?: string;
  documentTypeName?: string;
}

interface CoverageType {
  id: number;
  name: string;
}

const promptMappings = ref<PromptMappingDisplay[]>([]);
const selectedMapping = ref<PromptMappingDisplay | null>(null);
const loadingMappings = ref(false);
const prompts = ref<AIPrompt[]>([]);
const mappingModalRef = ref<InstanceType<typeof AddEditPromptMappingModal> | null>(null);

// Data from APIs
const coverageTypes = ref<CoverageType[]>([]);
const documentTypes = ref<DocumentType[]>([]);

// Cache flags to prevent unnecessary API calls
const promptsLoaded = ref(false);
const coverageTypesLoaded = ref(false);
const documentTypesLoaded = ref(false);

const selectedPromptName = computed(() => selectedMapping.value?.promptName || null);
const selectedCoverageTypeName = computed(() => selectedMapping.value?.coverageTypeName || null);
const selectedDocumentTypeName = computed(() => selectedMapping.value?.documentTypeName || null);

onMounted(async () => {
  try {
    // Load all data in parallel
    await Promise.all([loadPrompts(), loadCoverageTypes(), loadDocumentTypes()]);

    // Load mappings after reference data is loaded
    await loadPromptMappings();
  } catch (error) {
    console.error('Failed to load initial data:', error);
  }
});

const loadPrompts = async () => {
  if (promptsLoaded.value) return;

  try {
    prompts.value = await aiService.getAllPrompts();
    promptsLoaded.value = true;
  } catch (error) {
    console.error('Failed to load prompts:', error);
  }
};

const loadCoverageTypes = async () => {
  if (coverageTypesLoaded.value) return;

  try {
    const response = await referenceService.coverageTypes();
    // Convert LabelValue to CoverageType format
    coverageTypes.value = response.map((item: LabelValue) => ({
      id: item.value,
      name: item.label,
    }));
    coverageTypesLoaded.value = true;
  } catch (error) {
    console.error('Failed to load coverage types:', error);
  }
};

const loadDocumentTypes = async () => {
  if (documentTypesLoaded.value) return;

  try {
    documentTypes.value = await documentService.getDocumentTypes();
    documentTypesLoaded.value = true;
  } catch (error) {
    console.error('Failed to load document types:', error);
  }
};

const loadPromptMappings = async () => {
  loadingMappings.value = true;
  try {
    const mappings = await aiService.getPromptMappings();

    // Enrich mappings with names
    promptMappings.value = mappings.map((mapping) => {
      const prompt = prompts.value.find((p) => p.promptId === mapping.promptId && p.version === mapping.promptVersion);
      const coverageType = coverageTypes.value.find((ct) => ct.id === mapping.coverageTypeId);
      const documentType = documentTypes.value.find((dt) => dt.id === mapping.documentTypeId);

      return {
        ...mapping,
        promptName: prompt?.name || 'Unknown Prompt',
        coverageTypeName: coverageType?.name || 'Unknown Coverage',
        documentTypeName: documentType?.name || 'Unknown Document',
      };
    });
  } catch (error) {
    console.error('Failed to load prompt mappings:', error);
    promptMappings.value = [];
  } finally {
    loadingMappings.value = false;
  }
};

const handleMappingSelect = (mapping: PromptMappingDisplay) => {
  selectedMapping.value = mapping;
};

const handleDeleteMapping = async (mapping: PromptMappingDisplay, event: Event) => {
  event.stopPropagation();
  if (
    confirm(
      `Are you sure you want to delete this mapping?\n\nPrompt: ${mapping.promptName}\nCoverage: ${mapping.coverageTypeName}\nDocument: ${mapping.documentTypeName}`,
    )
  ) {
    try {
      await aiService.deletePromptMapping(mapping.id.toString());
      await loadPromptMappings();
      if (selectedMapping.value?.id === mapping.id) {
        selectedMapping.value = null;
      }
    } catch (error) {
      console.error('Failed to delete mapping:', error);
    }
  }
};

const handleAddMapping = () => {
  mappingModalRef.value?.show();
};

const handleEditMapping = (mapping: PromptMappingDisplay, event: Event) => {
  event.stopPropagation();
  mappingModalRef.value?.show(mapping);
};

const handleMappingSaved = async () => {
  await loadPromptMappings();
};
</script>

<template>
  <div class="tw:flex tw:flex-col tw:gap-4">
    <!-- Header -->
    <div class="tw:space-y-2 tw:text-center">
      <h1 class="tw:bg-clip-text tw:text-3xl tw:font-bold">📚 Prompt Mapping</h1>
      <p class="tw:text-md tw:mx-auto tw:max-w-2xl tw:text-slate-600">
        Link prompts with coverage type and document type
      </p>
    </div>

    <!-- Status Bar -->
    <Card class="tw:border-slate-200 tw:bg-white/80 tw:p-4 tw:backdrop-blur-sm">
      <div class="tw:flex tw:items-center tw:justify-between">
        <div class="tw:flex tw:items-center tw:gap-4">
          <div class="tw:flex tw:items-center tw:gap-2">
            <span class="tw:text-md tw:font-bold tw:text-slate-600">Mapping Configuration:</span>
          </div>
          <Separator orientation="vertical" class="tw:h-6" />
          <div class="tw:flex tw:items-center tw:gap-2">
            <span class="tw:text-xs tw:font-medium tw:text-slate-600">Prompt:</span>
            <Badge v-if="selectedPromptName" class="tw:text-center" variant="secondary">{{ selectedPromptName }}</Badge>
            <Badge v-else variant="outline">None selected</Badge>
          </div>
          <Separator orientation="vertical" class="tw:h-6" />
          <div class="tw:flex tw:items-center tw:gap-2">
            <span class="tw:text-xs tw:font-medium tw:text-slate-600">Coverage Type:</span>
            <Badge v-if="selectedCoverageTypeName" class="tw:text-center" variant="secondary">{{
              selectedCoverageTypeName
            }}</Badge>
            <Badge v-else variant="outline">None selected</Badge>
          </div>
          <Separator orientation="vertical" class="tw:h-6" />
          <div class="tw:flex tw:items-center tw:gap-2">
            <span class="tw:text-xs tw:font-medium tw:text-slate-600">Document Type:</span>
            <Badge v-if="selectedDocumentTypeName" class="tw:text-center" variant="secondary">{{
              selectedDocumentTypeName
            }}</Badge>
            <Badge v-else variant="outline">None selected</Badge>
          </div>
        </div>
      </div>
    </Card>

    <!-- Prompt Mappings Section -->
    <Card class="tw:border-slate-200 tw:bg-white/80 tw:p-6 tw:backdrop-blur-sm">
      <div class="tw:space-y-4">
        <div class="tw:flex tw:items-center tw:justify-between">
          <div>
            <h2 class="tw:mb-2 tw:text-lg tw:font-semibold">Prompt Mappings</h2>
            <p class="tw:text-sm tw:text-slate-600">Select a mapping to view its configuration</p>
          </div>
          <UIButton @click="handleAddMapping" variant="outline">
            <Plus class="tw:h-4 tw:w-4" />
            Add Mapping
          </UIButton>
        </div>

        <!-- Loading state -->
        <div v-if="loadingMappings" class="tw:flex tw:items-center tw:justify-center tw:py-8">
          <div class="tw:text-slate-500">Loading mappings...</div>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="promptMappings.length === 0"
          class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-8">
          <p class="tw:text-slate-500">No prompt mappings found</p>
          <p class="tw:mt-2 tw:text-sm tw:text-slate-400">Create your first mapping to get started</p>
        </div>

        <!-- Mappings list -->
        <div v-else class="tw:max-h-96 tw:space-y-2 tw:overflow-y-auto">
          <div
            v-for="mapping in promptMappings"
            :key="mapping.id"
            @click="handleMappingSelect(mapping)"
            :class="[
              'tw:group tw:cursor-pointer tw:rounded-lg tw:border tw:p-4 tw:transition-all',
              selectedMapping?.id === mapping.id
                ? 'tw:border-blue-500 tw:bg-blue-50'
                : 'tw:border-slate-200 tw:bg-white tw:hover:border-blue-300 tw:hover:bg-slate-50',
            ]">
            <div class="tw:flex tw:items-center tw:justify-between">
              <div class="tw:flex-1">
                <div class="tw:flex tw:items-center tw:gap-2">
                  <span class="tw:font-medium tw:text-slate-900">{{ mapping.promptName }}</span>
                </div>
                <div class="tw:ml-6 tw:mt-2 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                  <span class="tw:text-xs">Coverage:</span>
                  <Badge variant="outline" class="tw:text-xs">
                    {{ mapping.coverageTypeName }}
                  </Badge>
                  <span class="tw:text-xs">Document:</span>
                  <Badge variant="outline" class="tw:text-xs">
                    {{ mapping.documentTypeName }}
                  </Badge>
                </div>
              </div>
              <div class="tw:ml-2 tw:flex tw:items-center tw:gap-1">
                <button
                  @click="handleEditMapping(mapping, $event)"
                  class="tw:rounded tw:p-1 tw:text-slate-400 tw:opacity-0 tw:transition-all tw:hover:bg-blue-50 tw:hover:text-blue-600 tw:group-hover:opacity-100"
                  title="Edit mapping">
                  <Pencil class="tw:h-4 tw:w-4" />
                </button>
                <button
                  @click="handleDeleteMapping(mapping, $event)"
                  class="tw:rounded tw:p-1 tw:text-slate-400 tw:opacity-0 tw:transition-all tw:hover:bg-red-50 tw:hover:text-red-600 tw:group-hover:opacity-100"
                  title="Delete mapping">
                  <X class="tw:h-4 tw:w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>

    <!-- Main Content Area -->
    <Card class="tw:border-slate-200 tw:bg-white/80 tw:p-6 tw:backdrop-blur-sm">
      <div class="tw:flex tw:min-h-[400px] tw:items-center tw:justify-center">
        <div class="tw:text-center">
          <p class="tw:text-slate-500">Additional mapping configuration coming soon...</p>
        </div>
      </div>
    </Card>

    <!-- Prompt Mapping Modal -->
    <AddEditPromptMappingModal
      ref="mappingModalRef"
      :prompts="prompts"
      :coverage-types="coverageTypes"
      :document-types="documentTypes"
      @saved="handleMappingSaved" />
  </div>
</template>
